import {
  AprConfigWithCondition,
  PriceRulesEntry,
  CarPriceRule,
  PriceRulesRevision,
} from '@upstart/harmony/lib/legos/dealerTypes';
import {
  PriceRules,
  PriceRulesMetadata,
} from '@upstart/harmony/lib/legos/priceRulesTypes';
import { CLIENT_TYPE } from '@upstart/harmony/lib/constants';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import { TieredAprConfig } from 'components/PricingEditor/Tiers/types';
import { Action } from 'util/amplitudeEvents';

export interface ModelEntry {
  year: number;
  make: string;
  model: string;
}

export interface ModelYearRangeEntry {
  maxYear: number | undefined;
  minYear: number | undefined;
}

export interface TrimEntrySampleCar {
  id: string;
  stock: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  price?: number | null;
  msrp?: number | null;
  invoice?: number | null;
}

export interface TrimEntry extends ModelEntry {
  trim: string;
  vin: string;
  mfrModelCodes: {
    code: string;
    name: string;
  }[];
  sampleCar?: TrimEntrySampleCar;
}

export type PriceRulesEntryUpdater = (
  apply: (value: PriceRulesEntry) => void,
) => void;

export type DeletedAPRConfigs = (deletedAPRConfigID: string) => void;

// subtype of User that has all the props needed for the Pricing Matrix
export interface UserForPriceMatrix {
  id: number;
  username: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

// subtype of Dealer that has all the props needed for the Pricing Matrix
export interface DealerForPriceMatrix {
  id: string;
  brand?: string[];
  timezone?: string;
  priceRules?: PriceRules | null;
  updatedAt?: string;
  desking?: Record<string, unknown>;
  aprConfig?: AprConfigWithCondition | null;
  inStoreAprConfig?: AprConfigWithCondition | null;
}

export enum AllTrimVinFilter {
  'ALL',
  'TRIM',
  'VIN',
}

export interface DefaultRulesTableForUsedProps {
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  clientType: CLIENT_TYPE;
  condition: Condition;
  priceRules: PriceRulesEntry;
  updatePriceRules: PriceRulesEntryUpdater;
  priceRulesRevisions?: PriceRulesRevision[];
  dealerPriceRules?: PriceRules;
  defaultTieredAprConfig: {
    tieredAprConfig: TieredAprConfig | null;
  };
  setDefaultTieredAprConfig: React.Dispatch<
    React.SetStateAction<{
      tieredAprConfig: TieredAprConfig | null;
    }>
  >;
}

export interface ModelRulesTableForUsedProps {
  index: number;
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  modelPriceRule: CarPriceRule;
  clientType: CLIENT_TYPE;
  condition: Condition;
  priceRules: PriceRulesEntry;
  updatePriceRules: PriceRulesEntryUpdater;
  priceRulesRevisions?: PriceRulesRevision[];
  dealerPriceRules?: PriceRules;
  updateDeletedAPRConfigs: DeletedAPRConfigs;
  currentTieredAPR: {
    tieredAprConfig: TieredAprConfig | null;
  };
  setTieredAprConfig: (index: number, tieredAprConfig: TieredAprConfig) => void;
  deleteTieredAprConfig: (index: number) => void;
  errorMessagesForYearRangeEntry: {
    maxYear: string | null;
    minYear: string | null;
  };
  setErrorMessageForYearInput: (
    index: number,
    key: 'maxYear' | 'minYear',
    errorMessageForCarModel: string | null,
  ) => void;
  deleteModelYearRangeEntry: (index: number) => void;
  isNewModelYearRangeEntry: boolean;
}

export interface PricingMatrixEditorForUsedProps {
  dealerPriceRules?: PriceRules;
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  clientType: CLIENT_TYPE;
  condition: Condition;
  onChange: (isReset: boolean) => void;
  onSave: (
    dealerPriceRules: PriceRules & {
      metadata: PriceRulesMetadata;
    },
  ) => void;
  fetching: boolean;
  sendAmplitudeEvent: (
    client: CLIENT_TYPE,
    condition: Condition,
    action: Action,
  ) => void;
}
